import { MessageSourceProps, withMessages } from "react-message-source";
import React, { Component } from 'react';
import axios, { CancelTokenSource } from 'axios';
import { Dimmer, Grid, Loader, Modal } from 'semantic-ui-react';
import Input from 'components/final-form/Input';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { FormState } from 'final-form';
import { TextDto, UpsertTextDto } from 'ts-types/api.types';
import { deleteText, findTextById, getAllNonDeletedTexts, searchTexts } from 'services/textServices';
import { noop } from 'utils/functionUtils';
import { debounce } from 'ts-debounce';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import UpsertTextForm from 'components/settings/texts/UpsertTextForm';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PxGrid from 'components/layout/PxGrid';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import EditActionButton from 'components/EditActionButton';
import AddButton from 'components/AddButton';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import DeleteActionButton from 'components/DeleteActionButton';
import SearchResultComponent from 'components/SearchResultComponent';

interface TextFieldProps extends FormState {
  textSearchTerm?: string
}

interface Props extends MessageSourceProps {
}

interface State {
  foundTexts: Array<TextDto>,
  textsLoaded: boolean,
  text?: UpsertTextDto,
  textId?: number,
  addEditModalOpen: boolean,
  loading: boolean,
  searchValue: string,
  cancelTokenSource: CancelTokenSource
  isDefault: boolean,
}

class TextView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundTexts: [],
      textsLoaded: false,
      text: undefined,
      textId: undefined,
      addEditModalOpen: false,
      loading: false,
      searchValue: "",
      isDefault: false,
      cancelTokenSource
    }
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    const {cancelTokenSource} = this.state;

    this.mounted = false;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  openAddEditModal = (id?: number) => {
    const {cancelTokenSource} = this.state;

    if (id) {
      findTextById(id, cancelTokenSource)
          .then(response => this.showModal(id, response))
          .catch(noop);
    }
  };

  showModal = (id?: number, text?: UpsertTextDto) => {
    this.setState({
      addEditModalOpen: true,
      text: text,
      textId: id
    });
  };

  hideModal = () => {
    this.setState({
      addEditModalOpen: false,
      textId: undefined,
      text: undefined
    });
  };

  onSaveUpsertForm = () => {
    const {searchValue, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        textsLoaded: true
      })
    };

    searchTexts(searchValue, cancelTokenSource)
        .then(response => {
          this.setState({
            foundTexts: response
          });
        })
        .catch(noop)
        .finally(onFinally);

    this.hideModal();
  };

  deleteText = (id: number): void => {
    const {foundTexts, cancelTokenSource} = this.state;

    deleteText(id, cancelTokenSource)
        .then(response => {
          this.setState({
            foundTexts: foundTexts.filter(text => text.id != response.id)
          })
        })
        .catch(noop);
  };

  handleChange = debounce(({values}: TextFieldProps): void => {
    if (!this.mounted) {
      return;
    }

    const {textSearchTerm = ""} = values;
    const {cancelTokenSource} = this.state;

    const onSuccess = (response: Array<TextDto>) => {
      this.setState({
        foundTexts: response
      })
    };

    const onFinally = () => {
      this.setState({
        textsLoaded: true
      })
    };

    this.setState({
      textsLoaded: false,
      searchValue: textSearchTerm
    });

    if (textSearchTerm) {
      searchTexts(textSearchTerm, cancelTokenSource)
          .then(onSuccess)
          .catch(noop)
          .finally(onFinally);
    } else {
      getAllNonDeletedTexts(cancelTokenSource)
          .then(onSuccess)
          .catch(noop)
          .finally(onFinally);
    }
  }, 300);

  render(): React.ReactNode {
    const {textsLoaded} = this.state;
    return (
        <PraxStyledSegment>

          <div>
            {this.renderSearchTextsSegment()}
          </div>

          <StyledViewTableRow>
            {!textsLoaded ? this.renderLoader() : this.renderTextsSegment()}
          </StyledViewTableRow>

          {this.renderUpsertTextModal()}
        </PraxStyledSegment>
    );
  }

  renderSearchTextsSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={() => {
            }}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {foundTexts} = this.state;

    const numResults = foundTexts.length;

    return (
        <PxGrid>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("select")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="textSearchTerm"
                  component={Input}
                  fluid
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </PxGrid>
    );
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage("msgDelete")}
              onConfirm={() => this.deleteText(cellData)}
              popupMessage={getMessage("text.confirmDelete")}
          />
        </React.Fragment>
    );
  };

  textRowGetter = ({index}: any) => {
    const {foundTexts} = this.state;

    Object.assign(foundTexts[index], {index: index + 1});

    return foundTexts[index]
  };

  foundTextsRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundTexts} = this.state;
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(foundTexts[index].id)}
        >
          {columns}
        </div>
    );
  };

  renderTextsSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundTexts} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundTexts.length}
            rowGetter={this.textRowGetter}
            rowRenderer={this.foundTextsRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("text.description")),
                dataKey: "description"
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: this.actionsCellRenderer
              }
            ]}
        />
    );
  };

  renderLoader = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">{getMessage("text.loading")}</Loader>
        </Dimmer>
    );
  };

  renderUpsertTextModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, text, textId} = this.state;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={this.hideModal}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("header.navigation.texts")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertTextForm
                text={text}
                textId={textId}
                onSave={this.onSaveUpsertForm}
                onCancel={this.hideModal}
            />
          </FormModalContentContainer>
        </Modal>
    )
  }
}

export default withMessages(TextView);
